import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import {
  Trophy, Users, MapPin, Play, Eye, UserPlus, LogOut, X,
  ChevronDown, ChevronUp, Lock, Crown, Shield, Search,
  CheckCircle, Clock, Swords, AlertCircle, Edit2, Trash2
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import BracketVisualization from './BracketVisualization';
import './BracketTournament.css';

const BracketTournamentCard = ({ tournament, user, onUpdate, t, initialShowModal = false }) => {
  const [showBracket, setShowBracket] = useState(false);
  const [showTeamManager, setShowTeamManager] = useState(initialShowModal);
  const [showAllTeams, setShowAllTeams] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchPlayer, setSearchPlayer] = useState('');

  // Team formation state - requires all 4 teammates
  const [teammateNames, setTeammateNames] = useState(['', '', '', '']);
  const [customTeamName, setCustomTeamName] = useState('');
  const [formErrors, setFormErrors] = useState([]);

  // Edit teammates state (for captain to fix names)
  const [isEditingTeammates, setIsEditingTeammates] = useState(false);
  const [editTeammateNames, setEditTeammateNames] = useState([]);

  // Match configuration state (admin)
  const [showMatchConfig, setShowMatchConfig] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [matchGameMode, setMatchGameMode] = useState('');
  const [matchMap, setMatchMap] = useState('');
  const [matchRoomId, setMatchRoomId] = useState('');
  const [matchRoomUrl, setMatchRoomUrl] = useState('');
  const [isCustomMap, setIsCustomMap] = useState(false);
  const [customMapName, setCustomMapName] = useState('');

  // Maps per game mode
  const mapsByMode = {
    searchAndDestroy: [
      'Random', 'Express', 'Favela', 'Crossroads Strike', 'Kurohana Metropolis', 'Apocalypse',
      'Hacienda', 'Icebreaker', 'Khandor Hideout', 'Hardhat', 'Diesel', 'Combine', 'Frequency',
      'Collateral Strike', 'Zoo', 'Cheshire Park', 'Arsenal', 'Rush', 'Seaside', 'Armada Strike',
      'Vacant', 'Meltdown', 'Raid', 'Summit', 'Takeoff', 'Firing Range', 'Standoff', 'Crossfire',
      'Tunisia', 'Crash', 'Highrise', 'Slums', 'Suldal Harbor', 'Hovec Sawmill', 'Scrapyard 2019',
      'Dome', 'Hackney Yard', 'Terminal', 'Satellite', 'Coastal'
    ],
    teamDeathmatch: [
      'Random', 'Favela', 'Apocalypse', 'Khandor Hideout', 'Diesel', 'Crossroads Strike', 'Express',
      'Icebreaker', 'Vacant', 'Hovec Sawmill', 'Miami Strike', 'Hardhat', 'Hacienda', 'Zoo', 'Combine',
      'Frequency', 'Collateral Strike', 'Reclaim', 'Cage', 'Armada Strike', 'Arsenal', 'Rush',
      'Cheshire Park', 'Nuketown: Year Of The Dragon', 'Seaside', 'Raid', 'Firing Range', 'Nuketown',
      'Summit', 'Takeoff', 'Meltdown', 'Crash', 'Standoff', 'Crossfire', 'Hijacked', 'Killhouse',
      'Rust', 'Dome', 'Shipment', 'Shoot House', 'Scrapyard 2019', 'Slums', 'Suldal Harbor',
      'Terminal', 'Highrise', 'Monastery', 'Nuketown Russia', 'Satellite', 'Hackney Yard'
    ],
    domination: [
      'Random', 'Favela', 'Apocalypse', 'Express', 'Crossroads Strike', 'Kurohana Metropolis',
      'Khandor Hideout', 'Icebreaker', 'Vacant', 'Hacienda', 'Miami Strike', 'Hardhat', 'Frequency',
      'Cheshire Park', 'Combine', 'Collateral Strike', 'Zoo', 'Nuketown: Year Of The Dragon', 'Rush',
      'Diesel', 'Arsenal', 'Seaside', 'Armada Strike', 'Meltdown', 'Raid', 'Takeoff', 'Highrise',
      'Summit', 'Firing Range', 'Standoff', 'Crossfire', 'Crash', 'Nuketown', 'Rust', 'Suldal Harbor',
      'Dome', 'Slums', 'Hovec Sawmill', 'Scrapyard 2019', 'Shipment', 'Hackney Yard', 'Terminal',
      'Satellite', 'Shoot House', 'Nuketown Russia'
    ],
    hardpoint: [
      'Random', 'Express', 'Apocalypse', 'Kurohana Metropolis', 'Diesel', 'Crossroads Strike',
      'Hacienda', 'Icebreaker', 'Hardhat', 'Khandor Hideout', 'Miami Strike', 'Frequency',
      'Cheshire Park', 'Combine', 'Collateral Strike', 'Zoo', 'Arsenal', 'Rush', 'Armada Strike',
      'Nuketown: Year Of The Dragon', 'Seaside', 'Vacant', 'Firing Range', 'Nuketown', 'Raid',
      'Summit', 'Takeoff', 'Standoff', 'Crossfire', 'Crash', 'Hijacked', 'Rust', 'Suldal Harbor',
      'Shipment', 'Slums', 'Hovec Sawmill', 'Scrapyard 2019', 'Hackney Yard', 'Terminal',
      'Satellite', 'Shoot House', 'Nuketown Russia'
    ],
    killConfirmed: [
      'Random', 'Express', 'Favela', 'Crossroads Strike', 'Rush', 'Diesel', 'Hardhat', 'Hacienda',
      'Miami Strike', 'Apocalypse', 'Khandor Hideout', 'Zoo', 'Combine', 'Cage', 'Collateral Strike',
      'Reclaim', 'Armada Strike', 'Arsenal', 'Seaside', 'Frequency', 'Cheshire Park', 'Icebreaker',
      'Firing Range', 'Nuketown', 'Raid', 'Summit', 'Meltdown', 'Crash', 'Standoff', 'Rust',
      'Hijacked', 'Killhouse', 'Dome', 'Shipment', 'Slums', 'Vacant', 'Hovec Sawmill', 'Hackney Yard',
      'Highrise', 'Satellite', 'Shoot House', 'Nuketown Russia'
    ],
    frontline: [
      'Random', 'Armada Strike', 'Seaside', 'Rush', 'Crossroads Strike', 'Diesel', 'Combine',
      'Collateral Strike', 'Frequency', 'Nuketown: Year Of The Dragon', 'Cheshire Park', 'Express',
      'Hijacked', 'Summit', 'Killhouse', 'Crossfire', 'Crash', 'Scrapyard 2019', 'Miami Strike',
      'Nuketown Russia', 'Monastery', 'Oasis'
    ],
    searchAndRescue: [
      'Random', 'Hackney Yard', 'Terminal', 'Summit', 'Favela', 'Hacienda', 'Coastal', 'Crash',
      'Standoff', 'Crossfire', 'Takeoff', 'Raid', 'Tunisia'
    ]
  };

  // Get maps for current game mode
  const getCurrentMaps = () => {
    return mapsByMode[matchGameMode] || mapsByMode.searchAndDestroy || [];
  };

  // Applications modal state (admin)
  const [showApplications, setShowApplications] = useState(false);

  // Pending teams modal state (admin)
  const [showPendingTeams, setShowPendingTeams] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // Matchup configuration state (admin)
  const [showMatchupConfig, setShowMatchupConfig] = useState(false);
  const [matchupSelections, setMatchupSelections] = useState({});

  // Team swap state (in match config modal)
  const [showSwapSelect, setShowSwapSelect] = useState(null); // 'team1' or 'team2' or null
  const [swappingSlot, setSwappingSlot] = useState(null);

  const { bracketData } = tournament;
  const isAdmin = ['main_admin', 'manager', 'supervisor', 'approver'].includes(user?.role);

  // Handle both populated and non-populated creator
  const creatorId = tournament.creator?._id?.toString?.() || tournament.creator?.toString?.();
  const userId = user?._id?.toString?.() || user?._id;
  const isCreator = creatorId === userId;

  // Check if user is in a team
  const userTeam = bracketData.teams.find(team =>
    team.players.some(p => {
      const playerId = p.userId?._id?.toString?.() || p.userId?.toString?.();
      return playerId === userId;
    })
  );

  // Check if user is captain
  const isUserCaptain = userTeam?.players.find(p => {
    const playerId = p.userId?._id?.toString?.() || p.userId?.toString?.();
    return playerId === userId;
  })?.isCaptain;

  // Check if user has a pending application
  const userApplication = bracketData.teams.find(team =>
    (team.pendingApplications || []).some(app => {
      const appUserId = app.userId?._id?.toString?.() || app.userId?.toString?.();
      return appUserId === userId;
    })
  );

  // Count total pending applications (for admin badge)
  const totalPendingApplications = bracketData.teams.reduce((sum, team) =>
    sum + (team.pendingApplications?.length || 0), 0
  );

  // Count pending teams (teams with players but not approved)
  const pendingTeams = bracketData.teams.filter(team =>
    team.players.length > 0 && !team.isApproved
  );
  const totalPendingTeams = pendingTeams.length;

  // Count approved teams
  const approvedTeams = bracketData.teams.filter(team =>
    team.players.length > 0 && team.isApproved
  );
  const totalApprovedTeams = approvedTeams.length;

  // Helper function to get teams that have already battled in a specific round
  const getTeamsAlreadyBattledInRound = (roundNumber) => {
    if (!bracketData?.matches) return new Set();
    const completedMatchesInRound = bracketData.matches.filter(
      m => m.round === roundNumber && m.status === 'completed'
    );
    const teamsSet = new Set();
    completedMatchesInRound.forEach(m => {
      if (m.team1?.teamNumber) teamsSet.add(m.team1.teamNumber);
      if (m.team2?.teamNumber) teamsSet.add(m.team2.teamNumber);
    });
    return teamsSet;
  };

  // Helper function to get eliminated teams (losers from all completed matches)
  const getEliminatedTeams = () => {
    if (!bracketData?.matches) return new Set();
    const eliminatedTeams = new Set();
    bracketData.matches
      .filter(m => m.status === 'completed' && m.winner)
      .forEach(m => {
        // The loser is the team that's not the winner
        if (m.team1?.teamNumber && m.team1.teamNumber !== m.winner) {
          eliminatedTeams.add(m.team1.teamNumber);
        }
        if (m.team2?.teamNumber && m.team2.teamNumber !== m.winner) {
          eliminatedTeams.add(m.team2.teamNumber);
        }
      });
    return eliminatedTeams;
  };

  // Helper function to check if there are eligible teams for reroll (excluding current match teams, battled teams, and eliminated teams)
  const hasEligibleTeamsForReroll = (matchRound, currentTeam1Number, currentTeam2Number) => {
    const teamsAlreadyBattled = getTeamsAlreadyBattledInRound(matchRound);
    const eliminatedTeams = getEliminatedTeams();
    const eligibleTeams = approvedTeams.filter(t =>
      t.teamNumber !== currentTeam1Number &&
      t.teamNumber !== currentTeam2Number &&
      !teamsAlreadyBattled.has(t.teamNumber) &&
      !eliminatedTeams.has(t.teamNumber)
    );
    return eligibleTeams.length > 0;
  };

  // Check if user's team is pending approval
  const isUserTeamPending = userTeam && !userTeam.isApproved;

  // Check if user can join a specific team (name MUST be in invited list)
  const canJoinTeam = (team) => {
    if (team.players.length >= 5) return false;
    if (team.players.length === 0) return false; // Empty teams need to be created, not joined
    if (!team.invitedPlayers || team.invitedPlayers.length === 0) return false; // No invited list means no one can join

    // ALWAYS check if user's username or inGameName is in invited list
    // Trim and lowercase for proper comparison
    const userNames = [
      user?.username?.toLowerCase()?.trim(),
      user?.inGameName?.toLowerCase()?.trim()
    ].filter(Boolean);

    return team.invitedPlayers.some(invited => {
      const invitedLower = invited?.toLowerCase()?.trim();
      return userNames.includes(invitedLower);
    });
  };

  // Count registered players
  const totalRegistered = bracketData.teams.reduce((sum, t) => sum + t.players.length, 0);
  const maxPlayers = bracketData.totalTeams * 5;
  const teamsWithPlayers = bracketData.teams.filter(t => t.players.length > 0).length;

  // Validate teammate names
  const validateTeammates = () => {
    const errors = [];
    const filledNames = teammateNames.filter(n => n.trim() !== '');

    if (filledNames.length !== 4) {
      errors.push(t('need4Teammates', 'You must specify all 4 teammates'));
    }

    // Check for duplicates
    const lowerNames = filledNames.map(n => n.toLowerCase().trim());
    const uniqueNames = new Set(lowerNames);
    if (uniqueNames.size !== lowerNames.length) {
      errors.push(t('duplicateNames', 'Teammate names must be unique'));
    }

    // Check if user included themselves
    const userNames = [user?.username?.toLowerCase(), user?.inGameName?.toLowerCase()].filter(Boolean);
    if (lowerNames.some(n => userNames.includes(n))) {
      errors.push(t('dontIncludeYourself', "Don't include yourself in the teammate list"));
    }

    setFormErrors(errors);
    return errors.length === 0;
  };

  // Create team (user becomes captain with full teammate list) - auto-assigns team number
  const handleCreateTeam = async () => {
    if (!validateTeammates()) return;

    setLoading(true);
    try {
      const response = await axios.post(`/api/bracket-tournaments/${tournament._id}/create-team`, {
        teamName: customTeamName || undefined, // Backend will auto-assign team number and default name
        invitedPlayers: teammateNames.filter(n => n.trim() !== '').map(n => n.trim()) // Trim all names
      });
      toast.success(t('teamApplied', 'Team application submitted! Awaiting admin approval.'));
      if (onUpdate) onUpdate(response.data.tournament);
      setShowTeamManager(false);
      resetTeamForm();
    } catch (error) {
      toast.error(error.response?.data?.message || t('errorCreatingTeam', 'Error creating team'));
    } finally {
      setLoading(false);
    }
  };

  // Join or apply to existing team
  const handleJoinTeam = async (teamNumber) => {
    setLoading(true);
    try {
      const response = await axios.post(`/api/bracket-tournaments/${tournament._id}/join`, {
        teamNumber
      });

      // Check if user joined directly or just applied
      if (response.data.status === 'applied') {
        toast.success(t('applicationSubmitted', 'Application submitted! Waiting for admin approval.'));
      } else {
        toast.success(t('joinedTeam', 'Joined team successfully!'));
      }

      if (onUpdate) onUpdate(response.data.tournament);
      setShowTeamManager(false);
    } catch (error) {
      toast.error(error.response?.data?.message || t('errorJoining', 'Error joining team'));
    } finally {
      setLoading(false);
    }
  };

  // Leave tournament
  const handleLeave = async () => {
    if (!window.confirm(t('confirmLeave', 'Are you sure you want to leave?'))) return;

    setLoading(true);
    try {
      const response = await axios.post(`/api/bracket-tournaments/${tournament._id}/leave`);
      toast.success(t('leftTournament', 'Left tournament'));
      if (onUpdate) onUpdate(response.data.tournament);
    } catch (error) {
      toast.error(error.response?.data?.message || t('errorLeaving', 'Error leaving'));
    } finally {
      setLoading(false);
    }
  };

  // Kick player (captain only)
  const handleKickPlayer = async (playerId) => {
    if (!isUserCaptain || !userTeam) return;
    if (!window.confirm(t('confirmKick', 'Remove this player from your team?'))) return;

    setLoading(true);
    try {
      const response = await axios.post(`/api/bracket-tournaments/${tournament._id}/kick-player`, {
        teamNumber: userTeam.teamNumber,
        playerId
      });
      toast.success(t('playerKicked', 'Player removed from team'));
      if (onUpdate) onUpdate(response.data.tournament);
    } catch (error) {
      toast.error(error.response?.data?.message || t('errorKicking', 'Error removing player'));
    } finally {
      setLoading(false);
    }
  };

  // Start editing invited teammates (captain only)
  const handleStartEditTeammates = () => {
    if (!isUserCaptain || !userTeam) return;
    // Get current invited players who haven't joined yet
    const currentInvited = (userTeam.invitedPlayers || []).filter(invited =>
      !userTeam.players.some(p =>
        p.username?.toLowerCase() === invited.toLowerCase() ||
        p.inGameName?.toLowerCase() === invited.toLowerCase()
      )
    );
    // Pad to 4 slots (for players who haven't joined)
    const paddedNames = [...currentInvited];
    while (paddedNames.length < 4) paddedNames.push('');
    setEditTeammateNames(paddedNames.slice(0, 4));
    setIsEditingTeammates(true);
    setFormErrors([]);
  };

  // Save edited teammate names (captain only)
  const handleSaveEditTeammates = async () => {
    if (!isUserCaptain || !userTeam) return;

    // Validate - need at least the non-joined slots filled
    const filledNames = editTeammateNames.filter(n => n.trim() !== '');
    const joinedCount = userTeam.players.length;
    const neededSlots = 5 - joinedCount;

    if (filledNames.length < neededSlots) {
      setFormErrors([t('needMoreTeammates', `You need ${neededSlots} more teammate names`)]);
      return;
    }

    // Check for duplicates
    const lowerNames = filledNames.map(n => n.toLowerCase().trim());
    const uniqueNames = new Set(lowerNames);
    if (uniqueNames.size !== lowerNames.length) {
      setFormErrors([t('duplicateNames', 'Teammate names must be unique')]);
      return;
    }

    // Check if user included themselves
    const userNames = [user?.username?.toLowerCase(), user?.inGameName?.toLowerCase()].filter(Boolean);
    if (lowerNames.some(n => userNames.includes(n))) {
      setFormErrors([t('dontIncludeYourself', "Don't include yourself in the teammate list")]);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`/api/bracket-tournaments/${tournament._id}/update-invited`, {
        teamNumber: userTeam.teamNumber,
        invitedPlayers: filledNames.map(n => n.trim())
      });
      toast.success(t('teammatesUpdated', 'Teammate names updated!'));
      if (onUpdate) onUpdate(response.data.tournament);
      setIsEditingTeammates(false);
      setFormErrors([]);
    } catch (error) {
      toast.error(error.response?.data?.message || t('errorUpdating', 'Error updating teammates'));
    } finally {
      setLoading(false);
    }
  };

  // Approve application (admin only)
  const handleApproveApplication = async (teamNumber, applicantId, applicantName) => {
    if (!window.confirm(`Approve ${applicantName} to join Team ${teamNumber}?`)) return;

    setLoading(true);
    try {
      const response = await axios.post(`/api/bracket-tournaments/${tournament._id}/approve-application`, {
        teamNumber,
        applicantId
      });
      toast.success(t('applicationApproved', `${applicantName} approved!`));
      if (onUpdate) onUpdate(response.data.tournament);
    } catch (error) {
      toast.error(error.response?.data?.message || t('errorApproving', 'Error approving application'));
    } finally {
      setLoading(false);
    }
  };

  // Reject application (admin only)
  const handleRejectApplication = async (teamNumber, applicantId, applicantName) => {
    if (!window.confirm(`Reject ${applicantName}'s application to Team ${teamNumber}?`)) return;

    setLoading(true);
    try {
      const response = await axios.post(`/api/bracket-tournaments/${tournament._id}/reject-application`, {
        teamNumber,
        applicantId
      });
      toast.success(t('applicationRejected', 'Application rejected'));
      if (onUpdate) onUpdate(response.data.tournament);
    } catch (error) {
      toast.error(error.response?.data?.message || t('errorRejecting', 'Error rejecting application'));
    } finally {
      setLoading(false);
    }
  };

  // Approve team (admin only)
  const handleApproveTeam = async (teamNumber, teamName) => {
    if (!window.confirm(`Approve "${teamName || 'Team ' + teamNumber}" to participate in the tournament?`)) return;

    setLoading(true);
    try {
      const response = await axios.post(`/api/bracket-tournaments/${tournament._id}/approve-team`, {
        teamNumber
      });
      toast.success(t('teamApproved', `${teamName || 'Team ' + teamNumber} approved!`));
      if (onUpdate) onUpdate(response.data.tournament);
    } catch (error) {
      toast.error(error.response?.data?.message || t('errorApprovingTeam', 'Error approving team'));
    } finally {
      setLoading(false);
    }
  };

  // Reject team (admin only)
  const handleRejectTeam = async (teamNumber, teamName, reason = '') => {
    const confirmMsg = reason
      ? `Reject "${teamName || 'Team ' + teamNumber}"?\nReason: ${reason}`
      : `Reject "${teamName || 'Team ' + teamNumber}"? This will remove the team.`;

    if (!window.confirm(confirmMsg)) return;

    setLoading(true);
    try {
      const response = await axios.post(`/api/bracket-tournaments/${tournament._id}/reject-team`, {
        teamNumber,
        reason
      });
      toast.success(t('teamRejected', 'Team rejected and removed'));
      if (onUpdate) onUpdate(response.data.tournament);
      setRejectReason('');
    } catch (error) {
      toast.error(error.response?.data?.message || t('errorRejectingTeam', 'Error rejecting team'));
    } finally {
      setLoading(false);
    }
  };

  // Open match configuration modal (admin only)
  const handleOpenMatchConfig = (match) => {
    setSelectedMatch(match);
    setMatchGameMode('searchAndDestroy'); // Default
    setMatchMap('Random'); // Default to Random
    setMatchRoomId('');
    setMatchRoomUrl('');
    setIsCustomMap(false);
    setCustomMapName('');
    setShowMatchConfig(true);
  };

  // Handle game mode change - reset map to first option
  const handleMatchGameModeChange = (newMode) => {
    setMatchGameMode(newMode);
    const maps = mapsByMode[newMode] || mapsByMode.searchAndDestroy;
    setMatchMap(maps[0] || 'Random');
    setIsCustomMap(false);
    setCustomMapName('');
  };

  // Handle map selection change
  const handleMapChange = (value) => {
    if (value === '__custom__') {
      setIsCustomMap(true);
      setMatchMap('');
    } else {
      setIsCustomMap(false);
      setMatchMap(value);
      setCustomMapName('');
    }
  };

  // Configure and start match (admin only)
  const handleConfigureAndStartMatch = async () => {
    if (!selectedMatch) return;

    // Determine final map value
    const finalMap = isCustomMap ? customMapName : matchMap;

    if (!matchGameMode || !finalMap || !matchRoomId) {
      toast.error(t('fillAllFields', 'Please fill in all fields'));
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`/api/bracket-tournaments/${tournament._id}/configure-match`, {
        matchId: selectedMatch.matchId,
        gameMode: matchGameMode,
        map: finalMap,
        roomId: matchRoomId,
        roomUrl: matchRoomUrl || ''
      });
      toast.success(t('matchStarted', 'Match started!'));
      if (onUpdate) onUpdate(response.data.tournament);
      setShowMatchConfig(false);
      setSelectedMatch(null);
    } catch (error) {
      toast.error(error.response?.data?.message || t('errorStartingMatch', 'Error starting match'));
    } finally {
      setLoading(false);
    }
  };

  // Start tournament (admin only)
  const handleStart = async () => {
    if (!window.confirm(t('confirmStart', 'Start the tournament? Teams will be locked.'))) return;

    setLoading(true);
    try {
      const response = await axios.post(`/api/bracket-tournaments/${tournament._id}/start`);
      toast.success(t('tournamentStarted', 'Tournament started!'));
      if (onUpdate) onUpdate(response.data.tournament);
    } catch (error) {
      toast.error(error.response?.data?.message || t('errorStarting', 'Error starting tournament'));
    } finally {
      setLoading(false);
    }
  };

  // Set match winner (admin only)
  const handleSelectWinner = async (matchId, winnerTeamNumber) => {
    if (!window.confirm(t('confirmWinner', `Set Team ${winnerTeamNumber} as winner?`))) return;

    setLoading(true);
    try {
      const response = await axios.post(
        `/api/bracket-tournaments/${tournament._id}/match/${matchId}/winner`,
        { winnerTeamNumber }
      );
      toast.success(response.data.message);
      if (onUpdate) onUpdate(response.data.tournament);
    } catch (error) {
      toast.error(error.response?.data?.message || t('errorSettingWinner', 'Error setting winner'));
    } finally {
      setLoading(false);
    }
  };

  // Cancel tournament
  const handleCancel = async () => {
    const reason = window.prompt(t('cancelReason', 'Reason for cancellation:'));
    if (!reason) return;

    setLoading(true);
    try {
      const response = await axios.post(`/api/bracket-tournaments/${tournament._id}/cancel`, { reason });
      toast.success(t('tournamentCancelled', 'Tournament cancelled'));
      if (onUpdate) onUpdate(response.data.tournament);
    } catch (error) {
      toast.error(error.response?.data?.message || t('errorCancelling', 'Error cancelling'));
    } finally {
      setLoading(false);
    }
  };

  // Initialize matchup selections when opening modal
  const handleOpenMatchupConfig = () => {
    const firstRoundMatches = bracketData.matches.filter(m => m.round === 1);
    const initialSelections = {};

    // Use existing custom matchups if available, otherwise default to approval order
    const existingMatchups = bracketData.customMatchups || [];

    firstRoundMatches.forEach((match, idx) => {
      const existing = existingMatchups.find(m => m.matchId === match.matchId);
      if (existing) {
        initialSelections[match.matchId] = {
          team1: existing.team1Number,
          team2: existing.team2Number
        };
      } else {
        // Default: use approval order (sorted approved teams)
        const sortedApproved = [...approvedTeams].sort((a, b) => {
          const dateA = a.approvedAt ? new Date(a.approvedAt) : new Date(0);
          const dateB = b.approvedAt ? new Date(b.approvedAt) : new Date(0);
          return dateA - dateB;
        });
        const team1Idx = idx * 2;
        const team2Idx = idx * 2 + 1;
        initialSelections[match.matchId] = {
          team1: sortedApproved[team1Idx]?.teamNumber || null,
          team2: sortedApproved[team2Idx]?.teamNumber || null
        };
      }
    });

    setMatchupSelections(initialSelections);
    setShowMatchupConfig(true);
  };

  // Save matchup configuration (admin only)
  const handleSaveMatchups = async () => {
    const matchups = Object.entries(matchupSelections).map(([matchId, teams]) => ({
      matchId,
      team1Number: teams.team1,
      team2Number: teams.team2
    }));

    // Validate all matchups have both teams
    const incomplete = matchups.find(m => !m.team1Number || !m.team2Number);
    if (incomplete) {
      toast.error(t('selectBothTeams', 'Please select both teams for all matches'));
      return;
    }

    // Check for duplicate team usage
    const usedTeams = new Set();
    for (const m of matchups) {
      if (usedTeams.has(m.team1Number) || usedTeams.has(m.team2Number)) {
        toast.error(t('duplicateTeam', 'Each team can only be used once'));
        return;
      }
      usedTeams.add(m.team1Number);
      usedTeams.add(m.team2Number);
    }

    setLoading(true);
    try {
      const response = await axios.post(`/api/bracket-tournaments/${tournament._id}/configure-matchups`, {
        matchups
      });
      toast.success(t('matchupsConfigured', 'Matchups configured successfully!'));
      if (onUpdate) onUpdate(response.data.tournament);
      setShowMatchupConfig(false);
    } catch (error) {
      toast.error(error.response?.data?.message || t('errorConfiguringMatchups', 'Error configuring matchups'));
    } finally {
      setLoading(false);
    }
  };

  // Adjust bracket size (admin only)
  const handleAdjustSize = async (newSize) => {
    if (!window.confirm(t('confirmAdjustSize', `Adjust bracket to ${newSize} teams? This will regenerate the bracket structure.`))) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`/api/bracket-tournaments/${tournament._id}/adjust-size`, {
        newSize
      });
      toast.success(t('bracketAdjusted', `Bracket adjusted to ${newSize} teams`));
      if (onUpdate) onUpdate(response.data.tournament);
    } catch (error) {
      toast.error(error.response?.data?.message || t('errorAdjustingSize', 'Error adjusting bracket size'));
    } finally {
      setLoading(false);
    }
  };

  // Reroll team in match (random swap)
  const handleRerollTeam = async (slot) => {
    if (!selectedMatch) return;

    setSwappingSlot(slot);
    setLoading(true);
    try {
      const response = await axios.post(`/api/bracket-tournaments/${tournament._id}/swap-team`, {
        matchId: selectedMatch.matchId,
        slot,
        reroll: true
      });
      toast.success(response.data.message || t('teamSwapped', 'Team swapped!'));
      if (onUpdate) onUpdate(response.data.tournament);
      // Update selectedMatch with new data
      const updatedMatch = response.data.tournament.bracketData.matches.find(m => m.matchId === selectedMatch.matchId);
      if (updatedMatch) setSelectedMatch(updatedMatch);
    } catch (error) {
      toast.error(error.response?.data?.message || t('errorSwappingTeam', 'Error swapping team'));
    } finally {
      setLoading(false);
      setSwappingSlot(null);
    }
  };

  // Swap team with specific team
  const handleSwapWithTeam = async (slot, newTeamNumber) => {
    if (!selectedMatch) return;

    setSwappingSlot(slot);
    setLoading(true);
    try {
      const response = await axios.post(`/api/bracket-tournaments/${tournament._id}/swap-team`, {
        matchId: selectedMatch.matchId,
        slot,
        newTeamNumber
      });
      toast.success(response.data.message || t('teamSwapped', 'Team swapped!'));
      if (onUpdate) onUpdate(response.data.tournament);
      // Update selectedMatch with new data
      const updatedMatch = response.data.tournament.bracketData.matches.find(m => m.matchId === selectedMatch.matchId);
      if (updatedMatch) setSelectedMatch(updatedMatch);
      setShowSwapSelect(null);
    } catch (error) {
      toast.error(error.response?.data?.message || t('errorSwappingTeam', 'Error swapping team'));
    } finally {
      setLoading(false);
      setSwappingSlot(null);
    }
  };

  const resetTeamForm = () => {
    setTeammateNames(['', '', '', '']);
    setCustomTeamName('');
    setFormErrors([]);
    setSearchPlayer('');
  };

  // Get status display
  const getStatusDisplay = () => {
    if (tournament.status === 'cancelled') {
      return { text: t('cancelled', 'CANCELLED'), class: 'status-cancelled' };
    }
    if (bracketData.bracketStatus === 'registration') {
      return { text: t('registration', 'REGISTRATION OPEN'), class: 'status-registration' };
    }
    if (bracketData.bracketStatus === 'in_progress') {
      return { text: `${t('round', 'ROUND')} ${bracketData.currentRound}/${bracketData.totalRounds}`, class: 'status-active' };
    }
    if (bracketData.bracketStatus === 'completed') {
      return { text: t('completed', 'COMPLETED'), class: 'status-completed' };
    }
    return { text: tournament.status.toUpperCase(), class: '' };
  };

  // Get round name
  const getRoundName = (round, totalRounds) => {
    if (round === totalRounds) return t('final', 'Final');
    if (round === totalRounds - 1 && totalRounds > 2) return t('semiFinals', 'Semi-Finals');
    if (round === totalRounds - 2 && totalRounds > 3) return t('quarterFinals', 'Quarter-Finals');
    return `${t('round', 'Round')} ${round}`;
  };

  const status = getStatusDisplay();

  // Teams user can join (their name is in invited list)
  const teamsUserCanJoin = bracketData.teams.filter(t =>
    t.players.length > 0 && t.players.length < 5 && canJoinTeam(t)
  );

  // Teams user can apply to (not invited, has space, not already applied)
  const teamsUserCanApply = bracketData.teams.filter(t => {
    if (t.players.length === 0 || t.players.length >= 5) return false;
    if (canJoinTeam(t)) return false; // Already can join directly
    // Check if already applied
    const alreadyApplied = (t.pendingApplications || []).some(app => {
      const appUserId = app.userId?._id?.toString?.() || app.userId?.toString?.();
      return appUserId === userId;
    });
    return !alreadyApplied;
  });

  // Render team card for the all teams modal
  // Kick team handler (admin only)
  const handleKickTeam = async (teamNumber, teamName) => {
    const reason = window.prompt(t('enterKickReason', 'Enter reason for kicking/disqualifying this team:'));
    if (reason === null) return; // User cancelled

    setLoading(true);
    try {
      const response = await axios.post(`/api/bracket-tournaments/${tournament._id}/kick-team`, {
        teamNumber,
        reason: reason || 'Disqualified by admin'
      });
      toast.success(t('teamKicked', `Team ${teamName} has been kicked`));
      if (onUpdate) onUpdate(response.data.tournament);
    } catch (error) {
      toast.error(error.response?.data?.message || t('errorKickingTeam', 'Error kicking team'));
    } finally {
      setLoading(false);
    }
  };

  const renderTeamCard = (team) => {
    const isMyTeam = userTeam?.teamNumber === team.teamNumber;
    const userCanJoin = canJoinTeam(team) && !userTeam && tournament.status !== 'cancelled';
    const isPending = team.players.length > 0 && !team.isApproved;

    // Use displayNumber if approved, otherwise show internal teamNumber
    const displayTeamNumber = team.displayNumber || team.teamNumber;

    return (
      <div
        key={team.teamNumber}
        className={`team-full-card ${team.isEliminated ? 'eliminated' : ''} ${isMyTeam ? 'my-team' : ''} ${team.isLocked ? 'locked' : ''} ${isPending ? 'pending' : ''}`}
      >
        <div className="team-card-header">
          <div className="team-identity">
            <span className="team-badge">T{String(displayTeamNumber).padStart(2, '0')}</span>
            <span className="team-name-display">{team.teamName || `Team ${displayTeamNumber}`}</span>
            {team.isLocked && <Lock className="w-4 h-4 text-orange-400" />}
            {team.isEliminated && <span className="eliminated-badge">OUT</span>}
            {/* Team approval status badge */}
            {team.players.length > 0 && bracketData.bracketStatus === 'registration' && (
              isPending ? (
                <span className="team-status-badge pending">
                  <Clock className="w-3 h-3" /> {t('pending', 'Pending')}
                </span>
              ) : (
                <span className="team-status-badge approved">
                  <CheckCircle className="w-3 h-3" /> {t('approved', 'Approved')}
                </span>
              )
            )}
            {/* Admin kick button */}
            {isAdmin && team.players.length > 0 && tournament.status !== 'cancelled' && bracketData.bracketStatus !== 'completed' && (
              <button
                className="kick-team-btn"
                onClick={(e) => { e.stopPropagation(); handleKickTeam(team.teamNumber, team.teamName || `Team ${displayTeamNumber}`); }}
                title={t('kickTeam', 'Kick/Disqualify Team')}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
          <span className="team-player-count">{team.players.length}/5</span>
        </div>

        <div className="team-players-list">
          {team.players.map((player, idx) => (
            <div key={idx} className={`team-player-slot filled ${player.isCaptain ? 'captain' : ''}`}>
              {player.isCaptain && <Crown className="w-3 h-3 text-yellow-400" />}
              <span className="player-ign">{player.inGameName || player.username}</span>
              {isMyTeam && isUserCaptain && !player.isCaptain && bracketData.bracketStatus === 'registration' && (
                <button
                  className="kick-btn"
                  onClick={() => handleKickPlayer(player.userId?._id || player.userId)}
                  title={t('kickPlayer', 'Remove player')}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
          {/* Show invited players who haven't joined yet - ONLY to teammates or admins */}
          {team.invitedPlayers && team.players.length < 5 && (isMyTeam || isAdmin) && (
            team.invitedPlayers
              .filter(invited => !team.players.some(p =>
                p.username?.toLowerCase() === invited.toLowerCase() ||
                p.inGameName?.toLowerCase() === invited.toLowerCase()
              ))
              .slice(0, 5 - team.players.length)
              .map((invited, idx) => (
                <div key={`invited-${idx}`} className="team-player-slot waiting">
                  <Clock className="w-3 h-3" />
                  <span className="player-ign waiting-name">{invited}</span>
                  <span className="waiting-label">{t('waiting', 'waiting')}</span>
                </div>
              ))
          )}
          {/* Show empty slots - hide invited count from non-teammates/non-admins */}
          {(() => {
            const invitedNotJoined = (team.invitedPlayers || []).filter(invited =>
              !team.players.some(p =>
                p.username?.toLowerCase() === invited.toLowerCase() ||
                p.inGameName?.toLowerCase() === invited.toLowerCase()
              )
            ).length;
            // For teammates/admins: show remaining empty slots after invited
            // For others: show all empty slots (they can't see invited names)
            const emptyCount = (isMyTeam || isAdmin)
              ? Math.max(0, 5 - team.players.length - invitedNotJoined)
              : Math.max(0, 5 - team.players.length);

            return Array.from({ length: emptyCount }).map((_, idx) => (
              <div key={`empty-${idx}`} className="team-player-slot empty">
                <span className="empty-slot-text">{t('emptySlot', 'Empty Slot')}</span>
              </div>
            ));
          })()}
        </div>

        {userCanJoin && bracketData.bracketStatus === 'registration' && (
          <button
            className="join-team-btn"
            onClick={() => handleJoinTeam(team.teamNumber)}
            disabled={loading}
          >
            <UserPlus className="w-4 h-4" />
            {t('joinThisTeam', 'Join Team')}
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      <div className={`bracket-tournament-card ${tournament.status === 'cancelled' ? 'cancelled' : ''}`}>
        {/* Card Header */}
        <div className="bracket-card-header">
          <div className="bracket-card-title">
            <Trophy className="w-5 h-5" />
            <span>{t('multistepTournament', 'Multiplayer Multistep Tournament')}</span>
          </div>
          <div className={`bracket-card-status ${status.class}`}>
            {status.text}
          </div>
        </div>

        {/* Tournament ID */}
        <div className="tournament-id-row">
          <span className="tournament-id-label">ID:</span>
          <span
            className="tournament-id-value"
            onClick={() => {
              navigator.clipboard.writeText(tournament._id);
              toast.success(t('copied', 'Copied!'));
            }}
            title={t('clickToCopy', 'Click to copy')}
          >
            {tournament._id}
          </span>
        </div>

        {/* Card Info */}
        <div className="bracket-card-info">
          <div className="info-row">
            <Users className="w-4 h-4" />
            <span className="info-value">
              {teamsWithPlayers}/{bracketData.totalTeams} {t('teams', 'teams')} ({totalRegistered}/{maxPlayers} {t('players', 'players')})
            </span>
          </div>
          {/* Show approved/pending breakdown during registration */}
          {bracketData.bracketStatus === 'registration' && teamsWithPlayers > 0 && (
            <div className="info-row teams-breakdown">
              <CheckCircle className="w-4 h-4 text-green-400" />
              <span className="info-value">
                <span className="approved-count">{totalApprovedTeams} {t('approved', 'approved')}</span>
                {totalPendingTeams > 0 && (
                  <span className="pending-count"> • {totalPendingTeams} {t('pendingApproval', 'pending')}</span>
                )}
              </span>
            </div>
          )}
          <div className="info-row prize">
            <Trophy className="w-4 h-4" />
            <span className="info-value">
              {tournament.prizePool} {t('pts', 'pts')}
              <span className="prize-split">({bracketData.prizePerWinner} {t('each', 'each')})</span>
            </span>
          </div>
        </div>

        {/* Tournament Winner Display (for completed tournaments) */}
        {bracketData.bracketStatus === 'completed' && (
          <div className="tournament-winner-display">
            <div className="winner-header">
              <Crown className="w-5 h-5 text-yellow-400" />
              <span className="winner-label">{t('tournamentWinner', 'Tournament Winner')}</span>
            </div>
            {(() => {
              const finalMatch = bracketData.matches.find(m => m.nextMatchId === null && m.winner);
              if (finalMatch && finalMatch.winner) {
                const winningTeam = bracketData.teams.find(t => t.teamNumber === finalMatch.winner.teamNumber);
                return (
                  <div className="winner-team">
                    <span className="winner-team-name">{finalMatch.winner.teamName || `Team ${finalMatch.winner.teamNumber}`}</span>
                    {winningTeam && winningTeam.players && (
                      <div className="winner-players">
                        {winningTeam.players.slice(0, 5).map((p, idx) => (
                          <span key={idx} className="winner-player">
                            {p.isCaptain && '👑'}{p.inGameName || p.username}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return <span className="no-winner">{t('noWinnerYet', 'No winner determined')}</span>;
            })()}
          </div>
        )}

        {/* User Team Status */}
        {userTeam && (
          <div className={`user-team-status ${isUserTeamPending ? 'pending' : 'approved'}`}>
            <div className="user-team-info">
              <Shield className="w-4 h-4" />
              <span>{t('yourTeam', 'Your Team')}: <strong>{userTeam.teamName || `Team ${userTeam.teamNumber}`}</strong></span>
              {isUserCaptain && <Crown className="w-4 h-4 text-yellow-400" title={t('captain', 'Captain')} />}
            </div>
            <div className="team-status-actions">
              {/* Team approval status */}
              {isUserTeamPending ? (
                <div className="team-pending-indicator">
                  <Clock className="w-4 h-4" />
                  <span>{t('awaitingApproval', 'Awaiting Approval')}</span>
                </div>
              ) : (
                <div className="team-approved-indicator">
                  <CheckCircle className="w-4 h-4" />
                  <span>{t('approved', 'Approved')}</span>
                </div>
              )}
              {/* Edit teammates button - captain only during registration */}
              {isUserCaptain && bracketData.bracketStatus === 'registration' && (
                <button
                  className="edit-teammates-btn"
                  onClick={handleStartEditTeammates}
                  title={t('editTeammates', 'Edit teammate names')}
                >
                  <Edit2 className="w-4 h-4" />
                  <span>{t('editNames', 'Edit Names')}</span>
                </button>
              )}
              {/* Team is always locked - only invited players can join */}
              <div className="team-locked-indicator">
                <Lock className="w-4 h-4" />
                <span>{t('inviteOnly', 'Invite Only')}</span>
              </div>
            </div>
          </div>
        )}

        {/* User Application Status */}
        {userApplication && !userTeam && (
          <div className="user-application-status">
            <Clock className="w-4 h-4" />
            <span>{t('applicationPending', 'Your application to')} <strong>{userApplication.teamName || `Team ${userApplication.teamNumber}`}</strong> {t('isPending', 'is pending approval')}</span>
          </div>
        )}

        {/* Quick Actions Bar */}
        <div className="bracket-quick-actions">
          <button
            className="quick-action-btn view-all"
            onClick={() => setShowAllTeams(true)}
          >
            <Users className="w-4 h-4" />
            {t('viewAllTeams', 'All Teams & Matchups')}
          </button>

          <button
            className="quick-action-btn view-bracket"
            onClick={() => setShowBracket(true)}
          >
            <Swords className="w-4 h-4" />
            {t('viewBracket', 'Bracket')}
          </button>

          {/* Applications button (admin only, not on completed tournaments) */}
          {isAdmin && totalPendingApplications > 0 && bracketData.bracketStatus !== 'completed' && (
            <button
              className="quick-action-btn applications"
              onClick={() => setShowApplications(true)}
            >
              <UserPlus className="w-4 h-4" />
              {t('applications', 'Applications')}
              <span className="badge">{totalPendingApplications}</span>
            </button>
          )}

          {/* Pending Teams button (admin only, during registration) */}
          {isAdmin && bracketData.bracketStatus === 'registration' && totalPendingTeams > 0 && (
            <button
              className="quick-action-btn pending-teams"
              onClick={() => setShowPendingTeams(true)}
            >
              <Clock className="w-4 h-4" />
              {t('pendingTeams', 'Pending Teams')}
              <span className="badge">{totalPendingTeams}</span>
            </button>
          )}

          {/* Matchup Configuration button (admin only, during registration, 2+ approved teams) */}
          {isAdmin && bracketData.bracketStatus === 'registration' && totalApprovedTeams >= 2 && (
            <button
              className="quick-action-btn matchup-config"
              onClick={handleOpenMatchupConfig}
            >
              <Swords className="w-4 h-4" />
              {t('configureMatchups', 'Configure Matchups')}
            </button>
          )}
        </div>

        {/* Main Actions */}
        <div className="bracket-card-actions">
          {/* Apply to Tournament (if registration and not in team) */}
          {bracketData.bracketStatus === 'registration' && !userTeam && tournament.status !== 'cancelled' && (
            <button
              className="bracket-action-btn join"
              onClick={() => setShowTeamManager(true)}
              disabled={loading}
            >
              <UserPlus className="w-4 h-4" />
              {t('applyToTournament', 'Apply to Tournament')}
            </button>
          )}

          {/* Leave (if in team and registration) */}
          {bracketData.bracketStatus === 'registration' && userTeam && (
            <button
              className="bracket-action-btn leave"
              onClick={handleLeave}
              disabled={loading}
            >
              <LogOut className="w-4 h-4" />
              {t('leaveTeam', 'Leave Team')}
            </button>
          )}

          {/* Start (admin/creator only, during registration) - only show if approved teams = required */}
          {(isAdmin || isCreator) && bracketData.bracketStatus === 'registration' && tournament.status !== 'cancelled' && totalApprovedTeams >= 4 && (
            <>
              {/* Show adjust size button if approved teams don't match required */}
              {totalApprovedTeams !== bracketData.totalTeams && totalApprovedTeams >= 4 && (
                <div className="bracket-size-adjust">
                  <span className="size-hint">{t('adjustSizeTo', 'Adjust to')}:</span>
                  {[4, 8, 16].filter(size => size <= totalApprovedTeams && size !== bracketData.totalTeams).map(size => (
                    <button
                      key={size}
                      className="size-option-btn"
                      onClick={() => handleAdjustSize(size)}
                      disabled={loading}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              )}
              {/* Only show start if approved teams match required */}
              {totalApprovedTeams === bracketData.totalTeams && (
                <button
                  className="bracket-action-btn start"
                  onClick={handleStart}
                  disabled={loading}
                >
                  <Play className="w-4 h-4" />
                  {t('startTournament', 'Start')} ({totalApprovedTeams}/{bracketData.totalTeams})
                </button>
              )}
              {/* Show status if not ready */}
              {totalApprovedTeams !== bracketData.totalTeams && (
                <span className="teams-status-hint">
                  {totalApprovedTeams}/{bracketData.totalTeams} {t('teamsReady', 'teams ready')}
                </span>
              )}
            </>
          )}

          {/* Cancel (creator or admin only) */}
          {(isCreator || isAdmin) && tournament.status !== 'cancelled' && tournament.status !== 'completed' && (
            <button
              className="bracket-action-btn cancel"
              onClick={handleCancel}
              disabled={loading}
            >
              <X className="w-4 h-4" />
              {t('cancel', 'Cancel')}
            </button>
          )}
        </div>
      </div>

      {/* EDIT TEAMMATES MODAL (Captain only) */}
      {isEditingTeammates && ReactDOM.createPortal(
        <div className="bracket-modal-overlay" onClick={() => { setIsEditingTeammates(false); setFormErrors([]); }}>
          <div className="bracket-modal bracket-edit-teammates-modal" onClick={e => e.stopPropagation()}>
            <div className="bracket-modal-header">
              <h2><Edit2 className="w-5 h-5" /> {t('editTeammateNames', 'Edit Teammate Names')}</h2>
              <button className="bracket-modal-close" onClick={() => { setIsEditingTeammates(false); setFormErrors([]); }}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="edit-teammates-content">
              <p className="edit-hint">{t('editHint', 'Fix any typos in your teammate names. Only these players can join your team.')}</p>

              {/* Errors */}
              {formErrors.length > 0 && (
                <div className="form-errors">
                  {formErrors.map((err, i) => (
                    <div key={i} className="form-error">
                      <AlertCircle className="w-4 h-4" />
                      {err}
                    </div>
                  ))}
                </div>
              )}

              {/* Already joined players (read-only) */}
              <div className="joined-players-section">
                <label>{t('alreadyJoined', 'Already Joined')}</label>
                {userTeam.players.map((player, idx) => (
                  <div key={idx} className="joined-player-row">
                    {player.isCaptain && <Crown className="w-4 h-4 text-yellow-400" />}
                    <span>{player.inGameName || player.username}</span>
                    {player.isCaptain && <span className="captain-badge">{t('captain', 'Captain')}</span>}
                  </div>
                ))}
              </div>

              {/* Editable teammate names */}
              <div className="edit-teammates-section">
                <label>{t('waitingToJoin', 'Waiting to Join')} ({5 - userTeam.players.length} {t('slots', 'slots')})</label>
                {editTeammateNames.slice(0, 5 - userTeam.players.length).map((name, idx) => (
                  <div key={idx} className="teammate-input-row">
                    <span className="teammate-number">{userTeam.players.length + idx + 1}.</span>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => {
                        const newNames = [...editTeammateNames];
                        newNames[idx] = e.target.value;
                        setEditTeammateNames(newNames);
                        setFormErrors([]);
                      }}
                      placeholder={`${t('teammate', 'Teammate')} ${idx + 1}`}
                    />
                  </div>
                ))}
              </div>

              <div className="edit-actions">
                <button
                  className="cancel-edit-btn"
                  onClick={() => { setIsEditingTeammates(false); setFormErrors([]); }}
                >
                  {t('cancel', 'Cancel')}
                </button>
                <button
                  className="save-edit-btn"
                  onClick={handleSaveEditTeammates}
                  disabled={loading}
                >
                  <CheckCircle className="w-4 h-4" />
                  {loading ? t('saving', 'Saving...') : t('saveChanges', 'Save Changes')}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ALL TEAMS & MATCHUPS MODAL */}
      {showAllTeams && ReactDOM.createPortal(
        <div className="bracket-modal-overlay" onClick={() => setShowAllTeams(false)}>
          <div className="bracket-modal bracket-all-teams-modal" onClick={e => e.stopPropagation()}>
            <div className="bracket-modal-header">
              <h2><Users className="w-5 h-5" /> {t('allTeamsMatchups', 'All Teams & Matchups')}</h2>
              <button className="bracket-modal-close" onClick={() => setShowAllTeams(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="all-teams-content">
              {/* Tournament Progress */}
              <div className="tournament-progress-bar">
                <div className="progress-info">
                  <span>{t('teams', 'Teams')}: {teamsWithPlayers}/{bracketData.totalTeams}</span>
                  <span>{t('players', 'Players')}: {totalRegistered}/{maxPlayers}</span>
                  <span>{t('status', 'Status')}: {status.text}</span>
                </div>
              </div>

              {/* Rounds Overview */}
              <div className="rounds-overview">
                {Array.from({ length: bracketData.totalRounds }, (_, i) => i + 1).map(round => {
                  const roundMatches = bracketData.matches.filter(m => m.round === round);
                  const completedCount = roundMatches.filter(m => m.status === 'completed').length;

                  return (
                    <div
                      key={round}
                      className={`round-indicator ${round === bracketData.currentRound ? 'current' : ''} ${round < bracketData.currentRound ? 'completed' : ''}`}
                    >
                      <span className="round-name">{getRoundName(round, bracketData.totalRounds)}</span>
                      <span className="round-progress">{completedCount}/{roundMatches.length}</span>
                    </div>
                  );
                })}
              </div>

              {/* Round-by-Round Matchups */}
              <div className="matchups-by-round">
                {Array.from({ length: bracketData.totalRounds }, (_, i) => i + 1).map(round => {
                  const roundMatches = bracketData.matches.filter(m => m.round === round);

                  return (
                    <div key={round} className="round-section">
                      <h3 className="round-title">
                        {getRoundName(round, bracketData.totalRounds)}
                        <span className="match-count">({roundMatches.length} {roundMatches.length === 1 ? t('match', 'match') : t('matches', 'matches')})</span>
                      </h3>

                      <div className="round-matchups">
                        {roundMatches.map(match => {
                          const team1 = bracketData.teams.find(t => t.teamNumber === match.team1?.teamNumber);
                          const team2 = bracketData.teams.find(t => t.teamNumber === match.team2?.teamNumber);

                          return (
                            <div key={match.matchId} className={`matchup-card status-${match.status}`}>
                              <div className="matchup-header">
                                <span
                                  className="match-id copyable"
                                  onClick={() => {
                                    navigator.clipboard.writeText(match.matchId);
                                    toast.success(t('copied', 'Copied!'));
                                  }}
                                  title={t('clickToCopy', 'Click to copy')}
                                  style={{ cursor: 'pointer' }}
                                >{match.matchId}</span>
                                <span className={`match-status ${match.status}`}>
                                  {match.status === 'completed' && <CheckCircle className="w-3 h-3" />}
                                  {match.status === 'ready' && <Play className="w-3 h-3" />}
                                  {match.status === 'pending' && <Clock className="w-3 h-3" />}
                                  {match.status.toUpperCase()}
                                </span>
                              </div>

                              <div className="matchup-teams">
                                {/* Team 1 */}
                                <div className={`matchup-team ${match.winner?.teamNumber === match.team1?.teamNumber ? 'winner' : ''}`}>
                                  {match.team1?.teamNumber ? (
                                    <>
                                      <div className="team-header-row">
                                        {match.winner?.teamNumber === match.team1?.teamNumber && <Crown className="w-4 h-4 text-yellow-400" />}
                                        <span className="team-label">T{String(match.team1.teamNumber).padStart(2, '0')}</span>
                                        <span className="team-name">{match.team1.teamName || `Team ${match.team1.teamNumber}`}</span>
                                      </div>
                                      {team1 && (
                                        <div className="team-players-mini">
                                          {team1.players.map((p, i) => (
                                            <span key={i} className="player-mini" title={p.inGameName || p.username}>
                                              {p.isCaptain && '👑'}{(p.inGameName || p.username || '').substring(0, 8)}
                                            </span>
                                          ))}
                                          {team1.players.length < 5 && (
                                            <span className="slots-remaining">+{5 - team1.players.length} {t('slots', 'slots')}</span>
                                          )}
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <span className="tbd-label">{t('tbd', 'TBD')}</span>
                                  )}
                                </div>

                                <div className="matchup-vs">
                                  <Swords className="w-5 h-5" />
                                </div>

                                {/* Team 2 */}
                                <div className={`matchup-team ${match.winner?.teamNumber === match.team2?.teamNumber ? 'winner' : ''}`}>
                                  {match.team2?.teamNumber ? (
                                    <>
                                      <div className="team-header-row">
                                        {match.winner?.teamNumber === match.team2?.teamNumber && <Crown className="w-4 h-4 text-yellow-400" />}
                                        <span className="team-label">T{String(match.team2.teamNumber).padStart(2, '0')}</span>
                                        <span className="team-name">{match.team2.teamName || `Team ${match.team2.teamNumber}`}</span>
                                      </div>
                                      {team2 && (
                                        <div className="team-players-mini">
                                          {team2.players.map((p, i) => (
                                            <span key={i} className="player-mini" title={p.inGameName || p.username}>
                                              {p.isCaptain && '👑'}{(p.inGameName || p.username || '').substring(0, 8)}
                                            </span>
                                          ))}
                                          {team2.players.length < 5 && (
                                            <span className="slots-remaining">+{5 - team2.players.length} {t('slots', 'slots')}</span>
                                          )}
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <span className="tbd-label">{t('tbd', 'TBD')}</span>
                                  )}
                                </div>
                              </div>

                              {/* Admin: Start Match (ready status) */}
                              {isAdmin && match.status === 'ready' && (
                                <div className="admin-match-actions">
                                  <button
                                    className="start-match-btn"
                                    onClick={() => handleOpenMatchConfig(match)}
                                    disabled={loading}
                                  >
                                    <Play className="w-4 h-4" />
                                    {t('startMatch', 'Start Match')}
                                  </button>
                                </div>
                              )}

                              {/* Active Match Info */}
                              {match.status === 'active' && (
                                <div className="active-match-info">
                                  <div className="match-details">
                                    <span className="mode-label">{match.matchGameMode}</span>
                                    <span className="map-label">{match.matchMap}</span>
                                  </div>
                                  {(isAdmin || userTeam?.teamNumber === match.team1?.teamNumber || userTeam?.teamNumber === match.team2?.teamNumber) && (
                                    <div className="room-id-display">
                                      <span className="room-label">{t('roomId', 'Room ID')}:</span>
                                      <span className="room-value">{match.roomId}</span>
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Admin: Select Winner (active match) */}
                              {isAdmin && match.status === 'active' && (
                                <div className="admin-winner-select">
                                  <span className="hint">{t('selectWinner', 'Select Winner')}:</span>
                                  <button
                                    onClick={() => handleSelectWinner(match.matchId, match.team1.teamNumber)}
                                    disabled={loading}
                                  >
                                    T{match.team1.teamNumber}
                                  </button>
                                  <button
                                    onClick={() => handleSelectWinner(match.matchId, match.team2.teamNumber)}
                                    disabled={loading}
                                  >
                                    T{match.team2.teamNumber}
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* All Teams Grid - Only show APPROVED teams, sorted by displayNumber */}
              <div className="all-teams-section">
                <h3>{t('approvedTeams', 'Approved Teams')} ({totalApprovedTeams}/{bracketData.totalTeams})</h3>
                <div className="teams-grid-full">
                  {bracketData.teams
                    .filter(team => team.isApproved && team.players.length > 0)
                    .sort((a, b) => (a.displayNumber || 0) - (b.displayNumber || 0))
                    .map(team => renderTeamCard(team))}
                </div>
                {totalApprovedTeams === 0 && (
                  <div className="no-teams-message">
                    <AlertCircle className="w-6 h-6" />
                    <p>{t('noApprovedTeamsYet', 'No teams have been approved yet')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* TEAM MANAGER MODAL (Apply to Tournament) */}
      {showTeamManager && ReactDOM.createPortal(
        <div className="bracket-modal-overlay" onClick={() => { setShowTeamManager(false); resetTeamForm(); }}>
          <div className="bracket-modal bracket-team-manager-modal" onClick={e => e.stopPropagation()}>
            <div className="bracket-modal-header">
              <h2><Users className="w-5 h-5" /> {t('applyToTournament', 'Apply to Tournament')}</h2>
              <button className="bracket-modal-close" onClick={() => { setShowTeamManager(false); resetTeamForm(); }}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="team-manager-content">
              {/* Teams user can join (their name is in invited list) */}
              {teamsUserCanJoin.length > 0 && (
                <div className="join-section">
                  <h3>{t('teamsWaitingForYou', 'Teams Waiting for You')}</h3>
                  <p className="section-hint">{t('yourNameInvited', 'Your username is in these team invite lists')}</p>

                  <div className="available-teams-list">
                    {teamsUserCanJoin.map(team => (
                      <div key={team.teamNumber} className={`joinable-team invited ${!team.isApproved ? 'pending-team' : ''}`}>
                        <div className="team-info-row">
                          <span className="team-badge">T{String(team.teamNumber).padStart(2, '0')}</span>
                          <span className="team-name">{team.teamName || `Team ${team.teamNumber}`}</span>
                          <span className="team-slots">{team.players.length}/5</span>
                          {!team.isApproved && (
                            <span className="pending-badge">{t('pendingApproval', 'Pending')}</span>
                          )}
                        </div>
                        <div className="team-members">
                          {team.players.map((p, i) => (
                            <span key={i} className="member-tag">
                              {p.isCaptain && '👑'}{p.inGameName || p.username}
                            </span>
                          ))}
                        </div>
                        <button
                          className="join-btn highlighted"
                          onClick={() => handleJoinTeam(team.teamNumber)}
                          disabled={loading}
                        >
                          <CheckCircle className="w-4 h-4" />
                          {t('joinYourTeam', 'Join Your Team')}
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="divider">
                    <span>{t('or', 'OR')}</span>
                  </div>
                </div>
              )}

              {/* Apply to Other Teams (if not invited anywhere) */}
              {teamsUserCanApply.length > 0 && (
                <div className="apply-section">
                  <h3>{t('applyToTeam', 'Apply to Join a Team')}</h3>
                  <p className="section-hint">{t('applyHint', 'Request to join these teams - admin approval required')}</p>

                  <div className="available-teams-list">
                    {teamsUserCanApply.map(team => (
                      <div key={team.teamNumber} className={`joinable-team apply-team ${!team.isApproved ? 'pending-team' : ''}`}>
                        <div className="team-info-row">
                          <span className="team-badge">T{String(team.teamNumber).padStart(2, '0')}</span>
                          <span className="team-name">{team.teamName || `Team ${team.teamNumber}`}</span>
                          <span className="team-slots">{team.players.length}/5</span>
                          {!team.isApproved && (
                            <span className="pending-badge">{t('pendingApproval', 'Pending')}</span>
                          )}
                        </div>
                        <div className="team-members">
                          {team.players.map((p, i) => (
                            <span key={i} className="member-tag">
                              {p.isCaptain && '👑'}{p.inGameName || p.username}
                            </span>
                          ))}
                        </div>
                        <button
                          className="apply-btn"
                          onClick={() => handleJoinTeam(team.teamNumber)}
                          disabled={loading}
                        >
                          <UserPlus className="w-4 h-4" />
                          {t('applyToJoin', 'Apply to Join')}
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="divider">
                    <span>{t('or', 'OR')}</span>
                  </div>
                </div>
              )}

              {/* Create New Team */}
              <div className="create-section">
                <h3>{t('createNewTeam', 'Create a New Team')}</h3>
                <p className="section-hint">{t('createHintFull', 'Enter your 4 teammates - only they can join your team')}</p>

                {/* Errors */}
                {formErrors.length > 0 && (
                  <div className="form-errors">
                    {formErrors.map((err, i) => (
                      <div key={i} className="form-error">
                        <AlertCircle className="w-4 h-4" />
                        {err}
                      </div>
                    ))}
                  </div>
                )}

                {/* Team Name */}
                <div className="form-group">
                  <label>{t('teamName', 'Team Name')} ({t('optional', 'optional')})</label>
                  <input
                    type="text"
                    value={customTeamName}
                    onChange={(e) => setCustomTeamName(e.target.value)}
                    placeholder={t('enterTeamName', 'Enter team name...')}
                    maxLength={20}
                  />
                </div>

                {/* Your Name (Captain - read only) */}
                <div className="form-group">
                  <label>{t('captain', 'Captain')} ({t('you', 'You')})</label>
                  <div className="captain-display">
                    <Crown className="w-4 h-4 text-yellow-400" />
                    <span>{user?.inGameName || user?.username}</span>
                  </div>
                </div>

                {/* Teammate Inputs */}
                <div className="form-group teammates-section">
                  <label>{t('teammates', 'Teammates')} ({t('required', 'required')} - 4 {t('players', 'players')})</label>
                  <p className="teammates-hint">{t('teammatesHint', 'Enter exact usernames or in-game names')}</p>

                  {teammateNames.map((name, idx) => (
                    <div key={idx} className="teammate-input-row">
                      <span className="teammate-number">{idx + 2}.</span>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => {
                          const newNames = [...teammateNames];
                          newNames[idx] = e.target.value;
                          setTeammateNames(newNames);
                          setFormErrors([]);
                        }}
                        placeholder={`${t('teammate', 'Teammate')} ${idx + 1} ${t('username', 'username')}`}
                        className={formErrors.length > 0 && !name.trim() ? 'error' : ''}
                      />
                    </div>
                  ))}
                </div>

                {/* Apply to Tournament Button */}
                <div className="form-group apply-section-final">
                  <button
                    className="apply-tournament-btn"
                    onClick={() => handleCreateTeam()}
                    disabled={loading || bracketData.teams.filter(t => t.players.length === 0).length === 0}
                  >
                    <UserPlus className="w-5 h-5" />
                    {loading ? t('applying', 'Applying...') : t('applyToTournament', 'Apply to Tournament')}
                  </button>
                  <p className="apply-hint">{t('applyHintAdmin', 'Your team will be reviewed by an admin before approval')}</p>
                  {bracketData.teams.filter(t => t.players.length === 0).length === 0 && (
                    <div className="no-slots-message">
                      {t('allTeamsTaken', 'All team slots are taken!')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* BRACKET VISUALIZATION MODAL */}
      {showBracket && ReactDOM.createPortal(
        <div className="bracket-modal-overlay" onClick={() => setShowBracket(false)}>
          <div className="bracket-modal bracket-view-modal" onClick={e => e.stopPropagation()}>
            <div className="bracket-modal-header">
              <h2><Swords className="w-5 h-5" /> {t('tournamentBracket', 'Tournament Bracket')}</h2>
              <button className="bracket-modal-close" onClick={() => setShowBracket(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <BracketVisualization
              tournament={tournament}
              user={user}
              onSelectWinner={handleSelectWinner}
              t={t}
            />
          </div>
        </div>,
        document.body
      )}

      {/* APPLICATIONS MODAL (Admin only) */}
      {showApplications && isAdmin && ReactDOM.createPortal(
        <div className="bracket-modal-overlay" onClick={() => setShowApplications(false)}>
          <div className="bracket-modal bracket-applications-modal" onClick={e => e.stopPropagation()}>
            <div className="bracket-modal-header">
              <h2><UserPlus className="w-5 h-5" /> {t('pendingApplications', 'Pending Applications')} ({totalPendingApplications})</h2>
              <button className="bracket-modal-close" onClick={() => setShowApplications(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="applications-content">
              {bracketData.teams.filter(team => (team.pendingApplications?.length || 0) > 0).length === 0 ? (
                <div className="no-applications">
                  <CheckCircle className="w-8 h-8" />
                  <p>{t('noApplications', 'No pending applications')}</p>
                </div>
              ) : (
                bracketData.teams
                  .filter(team => (team.pendingApplications?.length || 0) > 0)
                  .map(team => (
                    <div key={team.teamNumber} className="team-applications">
                      <div className="team-header">
                        <span className="team-badge">T{String(team.teamNumber).padStart(2, '0')}</span>
                        <span className="team-name">{team.teamName || `Team ${team.teamNumber}`}</span>
                        <span className="slots-info">{team.players.length}/5 {t('players', 'players')}</span>
                      </div>

                      <div className="applications-list">
                        {(team.pendingApplications || []).map((app, idx) => {
                          const applicantId = app.userId?._id?.toString?.() || app.userId?.toString?.();
                          return (
                            <div key={idx} className="application-item">
                              <div className="applicant-info">
                                <span className="applicant-name">{app.inGameName || app.username}</span>
                                <span className="applied-time">
                                  {t('applied', 'Applied')} {new Date(app.appliedAt).toLocaleDateString()}
                                </span>
                              </div>
                              <div className="application-actions">
                                <button
                                  className="approve-btn"
                                  onClick={() => handleApproveApplication(team.teamNumber, applicantId, app.inGameName || app.username)}
                                  disabled={loading || team.players.length >= 5}
                                  title={team.players.length >= 5 ? t('teamFull', 'Team is full') : t('approve', 'Approve')}
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </button>
                                <button
                                  className="reject-btn"
                                  onClick={() => handleRejectApplication(team.teamNumber, applicantId, app.inGameName || app.username)}
                                  disabled={loading}
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* PENDING TEAMS MODAL (Admin only) */}
      {showPendingTeams && isAdmin && ReactDOM.createPortal(
        <div className="bracket-modal-overlay" onClick={() => setShowPendingTeams(false)}>
          <div className="bracket-modal bracket-pending-teams-modal" onClick={e => e.stopPropagation()}>
            <div className="bracket-modal-header">
              <h2><Clock className="w-5 h-5" /> {t('pendingTeamsApproval', 'Teams Awaiting Approval')} ({totalPendingTeams})</h2>
              <button className="bracket-modal-close" onClick={() => setShowPendingTeams(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="pending-teams-content">
              {/* Info Banner */}
              <div className="pending-teams-info">
                <AlertCircle className="w-5 h-5" />
                <div>
                  <p>{t('pendingTeamsInfo', 'Teams need approval before the tournament can start.')}</p>
                  <p className="stats">
                    <span className="approved">{totalApprovedTeams} {t('approved', 'approved')}</span>
                    <span className="separator">•</span>
                    <span className="pending">{totalPendingTeams} {t('pending', 'pending')}</span>
                    <span className="separator">•</span>
                    <span className="total">{bracketData.totalTeams} {t('totalSlots', 'total slots')}</span>
                  </p>
                </div>
              </div>

              {pendingTeams.length === 0 ? (
                <div className="no-pending-teams">
                  <CheckCircle className="w-8 h-8" />
                  <p>{t('allTeamsApproved', 'All teams have been approved!')}</p>
                </div>
              ) : (
                <div className="pending-teams-list">
                  {pendingTeams.map(team => (
                    <div key={team.teamNumber} className="pending-team-card">
                      <div className="pending-team-header">
                        <div className="team-identity">
                          <span className="team-badge">T{String(team.teamNumber).padStart(2, '0')}</span>
                          <span className="team-name">{team.teamName || `Team ${team.teamNumber}`}</span>
                        </div>
                        <span className="team-player-count">{team.players.length}/5 {t('players', 'players')}</span>
                      </div>

                      <div className="pending-team-players">
                        {team.players.map((player, idx) => (
                          <div key={idx} className={`pending-team-player ${player.isCaptain ? 'captain' : ''}`}>
                            {player.isCaptain && <Crown className="w-3 h-3" />}
                            <span>{player.inGameName || player.username}</span>
                          </div>
                        ))}
                      </div>

                      {/* Invited players - always show all */}
                      {team.invitedPlayers && team.invitedPlayers.length > 0 && (
                        <div className="pending-team-invited">
                          <span className="invited-label">{t('invited', 'Invited')}:</span>
                          <div className="invited-names">
                            {team.invitedPlayers.map((name, idx) => (
                              <span key={idx} className="invited-name">{name}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="pending-team-actions">
                        <button
                          className="approve-team-btn"
                          onClick={() => handleApproveTeam(team.teamNumber, team.teamName)}
                          disabled={loading}
                        >
                          <span>✓</span> {t('approveTeam', 'Approve')}
                        </button>
                        <button
                          className="reject-team-btn"
                          onClick={() => handleRejectTeam(team.teamNumber, team.teamName)}
                          disabled={loading}
                        >
                          <span>✕</span> {t('rejectTeam', 'Reject')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Approve All Button */}
              {pendingTeams.length > 1 && (
                <div className="approve-all-section">
                  <button
                    className="approve-all-btn"
                    onClick={async () => {
                      if (!window.confirm(t('confirmApproveAll', `Approve all ${totalPendingTeams} pending teams?`))) return;
                      for (const team of pendingTeams) {
                        await handleApproveTeam(team.teamNumber, team.teamName);
                      }
                    }}
                    disabled={loading}
                  >
                    <CheckCircle className="w-4 h-4" />
                    {t('approveAllTeams', 'Approve All Teams')} ({totalPendingTeams})
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* MATCH CONFIGURATION MODAL (Admin only) */}
      {showMatchConfig && isAdmin && selectedMatch && ReactDOM.createPortal(
        <div className="bracket-modal-overlay" onClick={() => { setShowMatchConfig(false); setSelectedMatch(null); }}>
          <div className="bracket-modal bracket-match-config-modal" onClick={e => e.stopPropagation()}>
            <div className="bracket-modal-header">
              <h2><Play className="w-5 h-5" /> {t('configureMatch', 'Configure Match')} - {selectedMatch.matchId}</h2>
              <button className="bracket-modal-close" onClick={() => { setShowMatchConfig(false); setSelectedMatch(null); }}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="match-config-content">
              <div className="match-teams-preview">
                <div className="team-preview-with-swap">
                  <div className="team-preview">
                    <span className="team-badge">T{String(selectedMatch.team1?.teamNumber).padStart(2, '0')}</span>
                    <span className="team-name">{selectedMatch.team1?.teamName}</span>
                  </div>
                  <div className="swap-buttons">
{hasEligibleTeamsForReroll(selectedMatch.round, selectedMatch.team1?.teamNumber, selectedMatch.team2?.teamNumber) && (
                    <button
                      className="swap-btn reroll"
                      onClick={() => handleRerollTeam('team1')}
                      disabled={loading || swappingSlot === 'team1'}
                      title={t('rerollMatchup', 'Random swap')}
                    >
                      🎲 {swappingSlot === 'team1' ? '...' : t('reroll', 'Reroll')}
                    </button>
                    )}
                    <button
                      className="swap-btn other"
                      onClick={() => setShowSwapSelect(showSwapSelect === 'team1' ? null : 'team1')}
                      disabled={loading}
                      title={t('otherMatchup', 'Select team to swap')}
                    >
                      ↔️ {t('other', 'Other')}
                    </button>
                  </div>
                  {showSwapSelect === 'team1' && (
                    <div className="swap-select-dropdown">
                      <p>{t('selectTeamToSwap', 'Select team to swap with:')}</p>
                      {(() => {
                        const teamsAlreadyBattled = getTeamsAlreadyBattledInRound(selectedMatch.round);
                        const eliminatedTeams = getEliminatedTeams();
                        return approvedTeams
                          .filter(t => t.teamNumber !== selectedMatch.team1?.teamNumber && !teamsAlreadyBattled.has(t.teamNumber) && !eliminatedTeams.has(t.teamNumber))
                          .map(team => (
                            <button
                              key={team.teamNumber}
                              className="swap-team-option"
                              onClick={() => handleSwapWithTeam('team1', team.teamNumber)}
                              disabled={loading}
                            >
                              T{String(team.teamNumber).padStart(2, '0')} - {team.teamName || `Team ${team.teamNumber}`}
                            </button>
                          ));
                      })()}
                    </div>
                  )}
                </div>
                <div className="vs-divider">
                  <Swords className="w-5 h-5" />
                  <span>VS</span>
                </div>
                <div className="team-preview-with-swap">
                  <div className="team-preview">
                    <span className="team-badge">T{String(selectedMatch.team2?.teamNumber).padStart(2, '0')}</span>
                    <span className="team-name">{selectedMatch.team2?.teamName}</span>
                  </div>
                  <div className="swap-buttons">
{hasEligibleTeamsForReroll(selectedMatch.round, selectedMatch.team1?.teamNumber, selectedMatch.team2?.teamNumber) && (
                    <button
                      className="swap-btn reroll"
                      onClick={() => handleRerollTeam('team2')}
                      disabled={loading || swappingSlot === 'team2'}
                      title={t('rerollMatchup', 'Random swap')}
                    >
                      🎲 {swappingSlot === 'team2' ? '...' : t('reroll', 'Reroll')}
                    </button>
                    )}
                    <button
                      className="swap-btn other"
                      onClick={() => setShowSwapSelect(showSwapSelect === 'team2' ? null : 'team2')}
                      disabled={loading}
                      title={t('otherMatchup', 'Select team to swap')}
                    >
                      ↔️ {t('other', 'Other')}
                    </button>
                  </div>
                  {showSwapSelect === 'team2' && (
                    <div className="swap-select-dropdown">
                      <p>{t('selectTeamToSwap', 'Select team to swap with:')}</p>
                      {(() => {
                        const teamsAlreadyBattled = getTeamsAlreadyBattledInRound(selectedMatch.round);
                        const eliminatedTeams = getEliminatedTeams();
                        return approvedTeams
                          .filter(t => t.teamNumber !== selectedMatch.team2?.teamNumber && !teamsAlreadyBattled.has(t.teamNumber) && !eliminatedTeams.has(t.teamNumber))
                          .map(team => (
                            <button
                              key={team.teamNumber}
                              className="swap-team-option"
                              onClick={() => handleSwapWithTeam('team2', team.teamNumber)}
                              disabled={loading}
                            >
                              T{String(team.teamNumber).padStart(2, '0')} - {team.teamName || `Team ${team.teamNumber}`}
                            </button>
                          ));
                      })()}
                    </div>
                  )}
                </div>
              </div>

              <div className="config-form">
                <div className="form-group">
                  <label>{t('gameMode', 'Game Mode')}</label>
                  <select
                    value={matchGameMode}
                    onChange={(e) => handleMatchGameModeChange(e.target.value)}
                  >
                    <option value="searchAndDestroy">Search and Destroy</option>
                    <option value="teamDeathmatch">Team Deathmatch</option>
                    <option value="domination">Domination</option>
                    <option value="hardpoint">Hardpoint</option>
                    <option value="killConfirmed">Kill Confirmed</option>
                    <option value="frontline">Frontline</option>
                    <option value="searchAndRescue">Search and Rescue</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>{t('map', 'Map')}</label>
                  <select
                    value={isCustomMap ? '__custom__' : matchMap}
                    onChange={(e) => handleMapChange(e.target.value)}
                  >
                    {getCurrentMaps().map((map, idx) => (
                      <option key={idx} value={map}>{map}</option>
                    ))}
                    <option value="__custom__">✏️ {t('customMap', 'Custom Map...')}</option>
                  </select>
                  {isCustomMap && (
                    <input
                      type="text"
                      value={customMapName}
                      onChange={(e) => setCustomMapName(e.target.value)}
                      placeholder={t('enterCustomMapName', 'Enter custom map name...')}
                      className="custom-map-input"
                      autoFocus
                    />
                  )}
                </div>

                <div className="form-group">
                  <label>{t('roomId', 'Room ID')}</label>
                  <input
                    type="text"
                    value={matchRoomId}
                    onChange={(e) => setMatchRoomId(e.target.value)}
                    placeholder={t('enterRoomId', 'Enter room ID/code...')}
                  />
                </div>

                <div className="form-group">
                  <label>{t('roomUrl', 'Room URL')} ({t('optional', 'Optional')})</label>
                  <input
                    type="url"
                    value={matchRoomUrl}
                    onChange={(e) => setMatchRoomUrl(e.target.value)}
                    placeholder={t('enterRoomUrl', 'https://... (optional link to join room)')}
                  />
                </div>
              </div>

              <div className="config-actions">
                <button
                  className="cancel-btn"
                  onClick={() => { setShowMatchConfig(false); setSelectedMatch(null); }}
                >
                  {t('cancel', 'Cancel')}
                </button>
                <button
                  className="start-btn"
                  onClick={handleConfigureAndStartMatch}
                  disabled={loading || !matchGameMode || (!matchMap && !customMapName) || !matchRoomId}
                >
                  <Play className="w-4 h-4" />
                  {loading ? t('starting', 'Starting...') : t('startMatch', 'Start Match')}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* MATCHUP CONFIGURATION MODAL (Admin only) */}
      {showMatchupConfig && isAdmin && ReactDOM.createPortal(
        <div className="bracket-modal-overlay" onClick={() => setShowMatchupConfig(false)}>
          <div className="bracket-modal matchup-config-modal" onClick={e => e.stopPropagation()}>
            <div className="bracket-modal-header">
              <h2><Swords className="w-5 h-5" /> {t('configureMatchups', 'Configure Matchups')}</h2>
              <button className="bracket-modal-close" onClick={() => setShowMatchupConfig(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="matchup-config-content">
              <div className="matchup-config-header">
                <h3>{t('firstRoundMatchups', 'First Round Matchups')}</h3>
                <p>{t('matchupHint', 'Select which teams face each other in the first round')}</p>
              </div>

              {bracketData.matches.filter(m => m.round === 1).map((match, idx) => (
                <div key={match.matchId} className="matchup-row">
                  <span className="matchup-label">{t('match', 'Match')} {idx + 1}</span>
                  <select
                    className="matchup-select"
                    value={matchupSelections[match.matchId]?.team1 || ''}
                    onChange={(e) => setMatchupSelections(prev => ({
                      ...prev,
                      [match.matchId]: { ...prev[match.matchId], team1: parseInt(e.target.value) || null }
                    }))}
                  >
                    <option value="">{t('selectTeam', 'Select Team')}</option>
                    {approvedTeams.map(team => (
                      <option key={team.teamNumber} value={team.teamNumber}>
                        {team.teamName || `Team ${team.teamNumber}`}
                      </option>
                    ))}
                  </select>
                  <span className="matchup-vs">VS</span>
                  <select
                    className="matchup-select"
                    value={matchupSelections[match.matchId]?.team2 || ''}
                    onChange={(e) => setMatchupSelections(prev => ({
                      ...prev,
                      [match.matchId]: { ...prev[match.matchId], team2: parseInt(e.target.value) || null }
                    }))}
                  >
                    <option value="">{t('selectTeam', 'Select Team')}</option>
                    {approvedTeams.map(team => (
                      <option key={team.teamNumber} value={team.teamNumber}>
                        {team.teamName || `Team ${team.teamNumber}`}
                      </option>
                    ))}
                  </select>
                </div>
              ))}

              <button
                className="matchup-save-btn"
                onClick={handleSaveMatchups}
                disabled={loading}
              >
                {loading ? t('saving', 'Saving...') : t('saveMatchups', 'Save Matchups')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default BracketTournamentCard;
